import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../../../features/langSlice';
import { PLANS, planFromDuration, focusKeysFromAreas, priceCalc, focusClaims } from './data/carouselLogic';
import { OfferSlide, PurposeSlide, ReplaceSlide, FocusSlide, HowToSlide, ClashSlide, LiveImageSlide } from './slides/Slides';

/* Builds the product-suggestion carousel slides (after the formula card), in order:
   1 offer (Abo plans only) · 2 main purpose · 3 what you replace · 4 top 3 focus areas · 5 results ·
   6 how to take · 7 release over time · 8 review (Anna) · 9 trust · 10 review (Chris).
   Slides that need data the customer does not have (price comparison, focus areas) are left out.
   Returns [{ key, node }]. Pass `suggestion` = undefined to get []. */
const useProductCarouselSlides = (suggestion) => {
  const lang = useSelector(selectCurrentLanguage) || 'de';
  const { planDuration, mainPurpose, symptoms, ingredients, focusAreas, onEditFormula, onViewFormula } = suggestion || {};
  const enabled = Boolean(suggestion);

  const planKey = planFromDuration(planDuration);
  const price = useMemo(() => (enabled ? priceCalc(ingredients, lang) : null), [enabled, ingredients, lang]);
  const focusKeys = useMemo(() => focusKeysFromAreas(focusAreas), [focusAreas]);
  const claims = useMemo(
    () => (enabled && focusKeys.length ? focusClaims(ingredients, focusKeys, lang, symptoms) : null),
    [enabled, ingredients, focusKeys, lang, symptoms],
  );

  return useMemo(() => {
    if (!enabled) return [];
    const slides = [];
    if (PLANS[planKey].subscription) slides.push({ key: 'offer', node: <OfferSlide planKey={planKey} /> });
    slides.push({ key: 'purpose', node: <PurposeSlide purpose={mainPurpose} /> });
    if (price?.show) slides.push({ key: 'replace', node: <ReplaceSlide price={price} /> });
    if (claims) slides.push({ key: 'focus', node: <FocusSlide claims={claims} onEditFormula={onEditFormula} onViewFormula={onViewFormula} /> });
    slides.push({ key: 'results', node: <LiveImageSlide name="results" /> });
    slides.push({ key: 'howto', node: <HowToSlide /> });
    slides.push({ key: 'clash', node: <ClashSlide /> });
    slides.push({ key: 'anna', node: <LiveImageSlide name="anna" review /> });
    slides.push({ key: 'trust', node: <LiveImageSlide name="trust" /> });
    slides.push({ key: 'chris', node: <LiveImageSlide name="chris" review /> });
    return slides;
  }, [enabled, planKey, mainPurpose, price, claims, onEditFormula, onViewFormula]);
};

export default useProductCarouselSlides;
